import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/utils";

import { fetchQualityOwners, QUALITY_OWNERS_QUERY_KEY } from "./casesApi";

const SELECT_CLASS_NAME = cn(
  "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
  "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
);

export function QualityOwnerSelect({
  id,
  value,
  currentLabel,
  disabled,
  onChange,
  ariaLabel = "Owner",
}: {
  id: string;
  value: string | null;
  currentLabel?: string | null;
  disabled?: boolean;
  onChange: (userId: string | null) => void;
  ariaLabel?: string;
}): JSX.Element {
  const ownersQuery = useQuery({
    queryKey: QUALITY_OWNERS_QUERY_KEY,
    queryFn: fetchQualityOwners,
  });

  const options = useMemo(() => {
    const users = ownersQuery.data?.users ?? [];
    const mapped = users.map((user) => ({
      userId: user.id,
      label: user.display_name?.trim() ? user.display_name : user.id,
    }));
    const current = value?.trim() ?? "";
    if (current !== "" && !mapped.some((option) => option.userId === current)) {
      mapped.unshift({
        userId: current,
        label: currentLabel?.trim() || current,
      });
    }
    return mapped;
  }, [currentLabel, ownersQuery.data?.users, value]);

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      className={SELECT_CLASS_NAME}
      value={value ?? ""}
      disabled={disabled || ownersQuery.isLoading}
      onChange={(event) => {
        const next = event.target.value;
        onChange(next === "" ? null : next);
      }}
    >
      <option value="">Unassigned</option>
      {options.map((option) => (
        <option key={option.userId} value={option.userId}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
